import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import axios, { AxiosResponse } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { IAiGenerateRowsResponse, IManagedDbRow, TableEditorService } from "../../../services/table-editor-service";
import {
  IColumnValueMap,
  IPendingChanges,
  IPendingRowInsert,
  rowPkStringFromRow,
  RowPkString
} from "../pending-changes-reducer";
import { buildTableEditorErrorAlert, createTableEditorAlert, ITableEditorAlert } from "../table-editor-errors";

// Columns we should never ship to the AI endpoint as part of the
// "current row" context. The endpoint is upstream of a third-party
// LLM call; cell values flow out of the user's tenancy on every
// request. We trust the backend to enforce its own sanitisation, but
// belt-and-braces: strip locally so binary blobs (multi-MB base64
// payloads — huge wire cost) and explicitly sensitive columns
// (passwords / tokens / PII that the schema already redacts from the
// grid render) never appear in the network payload at all.
const AI_STRIPPED_KUIKA_TYPES: ReadonlySet<string> = new Set<string>([
  "Image",
  "Signature",
  "FileContent",
  "ByteArray",
  "VarBinary",
  "Binary",
  "Bytea",
  // Object — opaque catch-all; can hold anything, including blobs.
  // The model can't use it meaningfully anyway.
  "Object"
]);

// Defensive cap on string-cell length sent to the AI. Anything past
// this length is more likely to be a paste / generated payload than
// a value the model needs to see for context, and shipping 100 KB
// of text per row request is just wire waste.
const AI_MAX_STRING_LENGTH: number = 1_000;

interface IBuildContextRowInput {
  source: IManagedDbRow;
  columns: IDatabaseColumnModel[];
  sensitiveHiddenColumns: readonly string[];
}

const buildAiContextRow = (input: IBuildContextRowInput): IManagedDbRow => {
  const { source, columns, sensitiveHiddenColumns } = input;
  const sensitive: Set<string> = new Set<string>(sensitiveHiddenColumns);
  const stripped: IManagedDbRow = {};
  for (const col of columns) {
    if (sensitive.has(col.name)) {
      continue;
    }
    if (AI_STRIPPED_KUIKA_TYPES.has(col.kuikaDataType ?? "")) {
      continue;
    }
    const value: unknown = source[col.name];
    if (value === undefined) {
      continue;
    }
    if (typeof value === "string" && value.length > AI_MAX_STRING_LENGTH) {
      // Don't ship the multi-KB string; mark it as elided so the model
      // knows there's a value but doesn't get the content.
      stripped[col.name] = `(${value.length} characters omitted)`;
      continue;
    }
    stripped[col.name] = value;
  }
  return stripped;
};

// KOA-5857 — AI fill / generate flows + post-generate highlight pulse.
//
// Three user-facing verbs share this hook:
//   * aiFillRow(pk)            — fill empty cells of a persisted row
//   * aiFillInsertRow(clientId) — fill empty cells of a pending insert
//   * aiGenerateRows(count, hint) — produce N new insert rows
//
// All three share one AbortController so a click-spam can only race once.
// Highlighting + scroll-to-first happens via a useEffect on the
// highlightedInsertIds set the parent passes through to the grid.

export type AiBusy = { kind: "row"; pk: RowPkString } | { kind: "insert"; clientId: string };

export interface IUseTableEditorAiProps {
  tableName: string;
  tableSchema?: string;
  isReadOnly: boolean;
  pending: IPendingChanges;
  rows: IManagedDbRow[];
  // Column schema — used to identify blob / Object types that should
  // be stripped from the AI request payload.
  columns: IDatabaseColumnModel[];
  primaryKeyColumns: string[];
  // Column names the backend redacts from the row read (passwords /
  // tokens / explicitly tagged sensitive fields). They never leave
  // the server with real values, but we belt-and-braces strip them
  // again here so even mock / stubbed columns don't leak into the
  // AI request body.
  sensitiveHiddenColumns: readonly string[];
  editCell: (pk: RowPkString, column: string, value: unknown, originalValue: unknown) => void;
  editInsertCell: (clientId: string, column: string, value: unknown) => void;
  addRowFromValues: (values: IColumnValueMap) => string;
}

export interface IUseTableEditorAiResult {
  aiBusy: AiBusy | null;
  aiNotice: ITableEditorAlert | null;
  setAiNotice: (notice: ITableEditorAlert | null) => void;
  isAiModalOpen: boolean;
  setIsAiModalOpen: (open: boolean) => void;
  highlightedInsertIds: Set<string>;
  aiFillRow: (pk: RowPkString) => Promise<void>;
  aiFillInsertRow: (clientId: string) => Promise<void>;
  aiGenerateRows: (count: number, hint: string) => Promise<void>;
}

export const useTableEditorAi = (props: IUseTableEditorAiProps): IUseTableEditorAiResult => {
  const {
    tableName,
    tableSchema,
    isReadOnly,
    pending,
    rows,
    columns,
    primaryKeyColumns,
    sensitiveHiddenColumns,
    editCell,
    editInsertCell,
    addRowFromValues
  } = props;

  // Latest pending state mirrored into a ref so async AI handlers
  // can read the CURRENT pending values when their response lands,
  // not the stale value captured by useCallback's closure at the
  // time the AI request fired. Used to detect concurrent manual
  // edits and skip the AI overwrite for cells the user has touched
  // since the request started (race-condition guard).
  const pendingRef = useRef<IPendingChanges>(pending);
  useEffect((): void => {
    pendingRef.current = pending;
  }, [pending]);

  // True when a cell is "still empty" — null / undefined / empty
  // string at the LATEST pending state. The AI promise is "fill
  // empty cells"; once the user types something into a cell the
  // AI must NOT overwrite. Reads from pendingRef (latest) so the
  // check reflects what the user has typed since the AI started.
  const isCellStillEmpty = (pk: RowPkString, persistedValue: unknown, column: string): boolean => {
    const update = pendingRef.current.updates.get(pk);
    const liveValue: unknown = update?.changes
      ? column in update.changes
        ? update.changes[column]
        : persistedValue
      : persistedValue;
    return liveValue === null || liveValue === undefined || liveValue === "";
  };
  const isInsertCellStillEmpty = (clientId: string, column: string): boolean => {
    const insert: IPendingRowInsert | undefined = pendingRef.current.inserts.find(
      (i: IPendingRowInsert): boolean => i.clientId === clientId
    );
    if (!insert) {
      return false;
    }
    const liveValue: unknown = insert.values[column];
    return liveValue === null || liveValue === undefined || liveValue === "";
  };

  const [aiBusy, setAiBusy] = useState<AiBusy | null>(null);
  const [aiNotice, setAiNotice] = useState<ITableEditorAlert | null>(null);
  const [isAiModalOpen, setIsAiModalOpen] = useState<boolean>(false);
  // Briefly highlights the rows just produced by "Generate rows with AI".
  // Without this, generated inserts can land below the fold on a populated
  // page and the user wonders if anything happened. The set drives both a
  // scroll-into-view for the first id (effect below) and a CSS pulse on
  // each matching <tr> (passed to the grid by the parent).
  const [highlightedInsertIds, setHighlightedInsertIds] = useState<Set<string>>(new Set());

  // Shared AbortController for in-flight AI calls (fill row, fill insert,
  // generate rows). Only one AI call runs at a time — aiBusy gates the
  // others — so a single ref is enough. Aborted on unmount so a stale
  // response can't setState on a torn-down editor.
  const aiAbortRef = useRef<AbortController | null>(null);

  // Aborts the in-flight controller (if any) and clears the ref. No
  // state mutation — safe to call from unmount cleanup where setState
  // would either no-op or leak intent past the component lifetime.
  const abortInFlightAi = useCallback((): void => {
    if (aiAbortRef.current) {
      aiAbortRef.current.abort();
      aiAbortRef.current = null;
    }
  }, []);

  // Used when STARTING a new AI flow. Aborts the previous one AND clears
  // aiBusy explicitly — without the clear, the OLD flow's finalizer can't
  // unset busy state (its `ref === controller` guard now fails because the
  // ref was reassigned by the new flow), and the user gets stuck in a
  // "disabled forever" AI menu state.
  const cancelInFlightAi = useCallback((): void => {
    abortInFlightAi();
    setAiBusy(null);
  }, [abortInFlightAi]);

  useEffect((): (() => void) => {
    return (): void => {
      // Unmount: abort only — setState on an unmounting component is
      // either a no-op (React 18+) or intent leak (older). The state
      // is about to disappear anyway; the network leg is what matters.
      abortInFlightAi();
    };
  }, [abortInFlightAi]);

  // Pending edits beat the persisted row for the LLM's purposes — if the
  // user typed "Acme" into the Name cell five seconds ago we want the
  // model to see it. Insert rows pull straight from pending.inserts[].values
  // (handled in aiFillInsertRow).
  const buildCurrentRow = useCallback(
    (pk: RowPkString): IManagedDbRow | null => {
      const row: IManagedDbRow | undefined = rows.find(
        (r: IManagedDbRow): boolean => rowPkStringFromRow(r, primaryKeyColumns) === pk
      );
      if (!row) {
        return null;
      }
      const update = pending.updates.get(pk);
      if (!update) {
        return { ...row };
      }
      return { ...row, ...update.changes };
    },
    [rows, primaryKeyColumns, pending]
  );

  const aiFillRow = useCallback(
    async (pk: RowPkString): Promise<void> => {
      if (isReadOnly) {
        return;
      }
      const currentRow = buildCurrentRow(pk);
      if (!currentRow) {
        return;
      }
      cancelInFlightAi();
      const controller = new AbortController();
      aiAbortRef.current = controller;
      setAiBusy({ kind: "row", pk });
      setAiNotice(null);
      try {
        // Strip blob / Object / sensitive columns and truncate long
        // strings BEFORE the request leaves the browser. The endpoint
        // sanitises too, but a multi-MB image base64 has no business
        // riding the wire to the AI service in the first place.
        const sanitisedRow: IManagedDbRow = buildAiContextRow({
          source: currentRow,
          columns,
          sensitiveHiddenColumns
        });
        const response = await TableEditorService.AiFillRow(
          tableName,
          { tableSchema, currentRow: sanitisedRow },
          controller.signal
        );
        if (controller.signal.aborted) {
          return;
        }
        const values = response.data.values ?? {};
        const proposedCount: number = Object.keys(values).length;
        if (proposedCount === 0) {
          setAiNotice(
            createTableEditorAlert({
              tone: "info",
              operation: "ai",
              title: "No AI suggestions",
              message: response.data.warnings?.[0] ?? "AI had no suggestions for this row."
            })
          );
          return;
        }
        // Persisted-row lookup feeds the originalValue thread for the
        // reducer's optimistic-concurrency snapshot. Same row we used to
        // build currentRow, just without the pending-update overlay.
        const persistedRow: IManagedDbRow | undefined = rows.find(
          (r: IManagedDbRow): boolean => rowPkStringFromRow(r, primaryKeyColumns) === pk
        );
        // Race guard: the user may have typed into a cell between
        // request and response. AI promise is "fill empty cells";
        // honor that promise against the CURRENT pending state, not
        // the snapshot we sent. A column the user has since edited
        // stays as the user typed.
        let skippedCount: number = 0;
        Object.entries(values).forEach(([column, value]: [string, unknown]): void => {
          if (!isCellStillEmpty(pk, persistedRow?.[column] ?? null, column)) {
            skippedCount += 1;
            return;
          }
          editCell(pk, column, value, persistedRow?.[column] ?? null);
        });
        const appliedCount: number = proposedCount - skippedCount;
        // Append a "kept your edits" note when the race guard
        // skipped at least one column. Users who didn't type during
        // the request never see the suffix; users who did see why
        // their typing wasn't overwritten.
        const skipSuffix: string =
          skippedCount > 0 ? ` (kept your edits on ${skippedCount} cell${skippedCount === 1 ? "" : "s"})` : "";
        const filledCopy: string = `Filled ${appliedCount} cell${appliedCount === 1 ? "" : "s"}${skipSuffix}.`;
        if (response.data.warnings && response.data.warnings.length > 0) {
          setAiNotice(
            createTableEditorAlert({
              tone: "warn",
              operation: "ai",
              title: "AI filled the row with warnings",
              message: filledCopy,
              details: response.data.warnings
            })
          );
        } else if (appliedCount === 0) {
          setAiNotice(
            createTableEditorAlert({
              tone: "info",
              operation: "ai",
              title: "No AI changes applied",
              message: `${proposedCount} cell${proposedCount === 1 ? "" : "s"} were already filled by your edits while the request ran.`
            })
          );
        } else {
          setAiNotice(
            createTableEditorAlert({
              tone: "success",
              operation: "ai",
              title: "AI filled the row",
              message: `${filledCopy} Review and Save when ready.`
            })
          );
        }
      } catch (error: unknown) {
        if (axios.isCancel(error) || controller.signal.aborted) {
          return;
        }
        const alert: ITableEditorAlert = await buildTableEditorErrorAlert(error, {
          operation: "ai",
          title: "AI fill failed",
          fallbackMessage: "AI could not fill this row."
        });
        setAiNotice(alert);
      } finally {
        if (aiAbortRef.current === controller) {
          setAiBusy(null);
          aiAbortRef.current = null;
        }
      }
    },
    [
      tableName,
      tableSchema,
      buildCurrentRow,
      isReadOnly,
      rows,
      primaryKeyColumns,
      columns,
      sensitiveHiddenColumns,
      editCell,
      cancelInFlightAi
    ]
  );

  // Same shape as aiFillRow but for a pending insert. Differs in two
  // places only — currentRow comes straight from insert.values, and we
  // dispatch via editInsertCell so the values land back on the insert
  // (not a phantom update with no matching persisted row).
  const aiFillInsertRow = useCallback(
    async (clientId: string): Promise<void> => {
      if (isReadOnly) {
        return;
      }
      const insert: IPendingRowInsert | undefined = pending.inserts.find(
        (i: IPendingRowInsert): boolean => i.clientId === clientId
      );
      if (!insert) {
        return;
      }
      cancelInFlightAi();
      const controller = new AbortController();
      aiAbortRef.current = controller;
      setAiBusy({ kind: "insert", clientId });
      setAiNotice(null);
      try {
        const sanitisedRow: IManagedDbRow = buildAiContextRow({
          source: { ...insert.values },
          columns,
          sensitiveHiddenColumns
        });
        const response = await TableEditorService.AiFillRow(
          tableName,
          { tableSchema, currentRow: sanitisedRow },
          controller.signal
        );
        if (controller.signal.aborted) {
          return;
        }
        const values = response.data.values ?? {};
        const proposedCount: number = Object.keys(values).length;
        if (proposedCount === 0) {
          setAiNotice(
            createTableEditorAlert({
              tone: "info",
              operation: "ai",
              title: "No AI suggestions",
              message: response.data.warnings?.[0] ?? "AI had no suggestions for this row."
            })
          );
          return;
        }
        // Race guard — same shape as the persisted-row path. Don't
        // overwrite columns the user has typed into while the AI
        // request was in flight.
        let skippedCount: number = 0;
        Object.entries(values).forEach(([column, value]: [string, unknown]): void => {
          if (!isInsertCellStillEmpty(clientId, column)) {
            skippedCount += 1;
            return;
          }
          editInsertCell(clientId, column, value);
        });
        const appliedCount: number = proposedCount - skippedCount;
        const skipSuffix: string =
          skippedCount > 0 ? ` (kept your edits on ${skippedCount} cell${skippedCount === 1 ? "" : "s"})` : "";
        const filledCopy: string = `Filled ${appliedCount} cell${appliedCount === 1 ? "" : "s"}${skipSuffix}.`;
        if (response.data.warnings && response.data.warnings.length > 0) {
          setAiNotice(
            createTableEditorAlert({
              tone: "warn",
              operation: "ai",
              title: "AI filled the new row with warnings",
              message: filledCopy,
              details: response.data.warnings
            })
          );
        } else if (appliedCount === 0) {
          setAiNotice(
            createTableEditorAlert({
              tone: "info",
              operation: "ai",
              title: "No AI changes applied",
              message: `${proposedCount} cell${proposedCount === 1 ? "" : "s"} were already filled by your edits while the request ran.`
            })
          );
        } else {
          setAiNotice(
            createTableEditorAlert({
              tone: "success",
              operation: "ai",
              title: "AI filled the new row",
              message: `${filledCopy} Review and Save when ready.`
            })
          );
        }
      } catch (error: unknown) {
        if (axios.isCancel(error) || controller.signal.aborted) {
          return;
        }
        const alert: ITableEditorAlert = await buildTableEditorErrorAlert(error, {
          operation: "ai",
          title: "AI fill failed",
          fallbackMessage: "AI could not fill this row."
        });
        setAiNotice(alert);
      } finally {
        if (aiAbortRef.current === controller) {
          setAiBusy(null);
          aiAbortRef.current = null;
        }
      }
    },
    [
      tableName,
      tableSchema,
      pending.inserts,
      isReadOnly,
      columns,
      sensitiveHiddenColumns,
      editInsertCell,
      cancelInFlightAi
    ]
  );

  const aiGenerateRows = useCallback(
    async (count: number, hint: string): Promise<void> => {
      if (isReadOnly) {
        throw new Error("This table is read-only.");
      }
      cancelInFlightAi();
      const controller = new AbortController();
      aiAbortRef.current = controller;
      let response: AxiosResponse<IAiGenerateRowsResponse> | undefined;
      try {
        response = await TableEditorService.AiGenerateRows(
          tableName,
          {
            tableSchema,
            count,
            hint: hint.trim().length > 0 ? hint.trim() : undefined
          },
          controller.signal
        );
      } catch (error: unknown) {
        if (axios.isCancel(error) || controller.signal.aborted) {
          return;
        }
        throw error;
      } finally {
        if (aiAbortRef.current === controller) {
          aiAbortRef.current = null;
        }
      }
      if (controller.signal.aborted || !response) {
        return;
      }
      const payload: IAiGenerateRowsResponse = response.data;
      const generated = payload.rows ?? [];
      if (generated.length === 0) {
        throw new Error(payload.warnings?.[0] ?? "AI returned no usable rows.");
      }
      const newClientIds: string[] = generated.map((rowValues: IManagedDbRow): string => addRowFromValues(rowValues));
      // Trigger the scroll-into-view + pulse on the new rows. Effect below
      // owns the timeout-driven clear so multiple back-to-back generations
      // each get their own animation window.
      setHighlightedInsertIds(new Set(newClientIds));
      if (payload.warnings && payload.warnings.length > 0) {
        setAiNotice(
          createTableEditorAlert({
            tone: "warn",
            operation: "ai",
            title: "AI generated rows with warnings",
            message: `Generated ${generated.length} row${generated.length === 1 ? "" : "s"}. Review and Save when ready.`,
            details: payload.warnings
          })
        );
      } else {
        setAiNotice(
          createTableEditorAlert({
            tone: "success",
            operation: "ai",
            title: "AI generated rows",
            message: `Generated ${generated.length} row${generated.length === 1 ? "" : "s"}. Review and Save when ready.`
          })
        );
      }
    },
    [tableName, tableSchema, isReadOnly, addRowFromValues, cancelInFlightAi]
  );

  // When new rows are highlighted, scroll the first one into view and
  // clear the highlight after the pulse animation has run. CSS.escape
  // guards the querySelector against tmp-ids that include odd chars (they
  // don't today but it's the right primitive to use). querySelector finds
  // the row by the data-client-id attribute the grid already stamps on
  // insert <tr>s.
  useEffect((): (() => void) | undefined => {
    if (highlightedInsertIds.size === 0) {
      return undefined;
    }
    const firstId: string = Array.from(highlightedInsertIds)[0]!;
    const raf: number = window.requestAnimationFrame((): void => {
      const el = document.querySelector(`tr[data-client-id="${CSS.escape(firstId)}"]`);
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
    const clear: number = window.setTimeout((): void => {
      setHighlightedInsertIds(new Set());
    }, 2200);
    return (): void => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(clear);
    };
  }, [highlightedInsertIds]);

  return {
    aiBusy,
    aiNotice,
    setAiNotice,
    isAiModalOpen,
    setIsAiModalOpen,
    highlightedInsertIds,
    aiFillRow,
    aiFillInsertRow,
    aiGenerateRows
  };
};
